import { getAccessToken } from '@/shared/lib/auth'

// The Mastra agents surfaced in the UI. Each id maps 1:1 to a server-side agent that the
// durable-agent endpoints (`/agents/:agentId/stream` | `/observe`) and the memory endpoints
// are scoped by. `supervisor` is the default ("General") agent.
export const MASTRA_AGENT_IDS = ['supervisor', 'form-builder', 'risk-scoring'] as const
export type MastraAgentId = (typeof MASTRA_AGENT_IDS)[number]

export const DEFAULT_MASTRA_AGENT_ID: MastraAgentId = 'supervisor'

export const isMastraAgentId = (value: unknown): value is MastraAgentId =>
  typeof value === 'string' && (MASTRA_AGENT_IDS as readonly string[]).includes(value)

// Normalise an unknown route param to a valid agent id, falling back to the default so an
// unrecognised URL still lands on a working chat instead of breaking the endpoints.
export const toMastraAgentId = (value: unknown): MastraAgentId =>
  isMastraAgentId(value) ? value : DEFAULT_MASTRA_AGENT_ID

// Durable-agent endpoints. Durable agents (e.g. the supervisor) keep executing server-side as
// a background task even if the client disconnects, so a run started via `stream` can be
// reconnected to via `observe`, which replays the cached events.
export const agentStreamPath = (agentId: string): string => `/agents/${agentId}/stream`
export const agentObservePath = (agentId: string): string => `/agents/${agentId}/observe`

// How the chat machinery reaches Mastra. Staff chats use the authenticated proxy
// (`mastraFetch`); the public user-client chat swaps in a token-based fetcher.
export type MastraFetcher = (path: string, init?: RequestInit) => Promise<Response>

const buildUrl = (path: string): string => `${import.meta.env.PUBLIC_API_URL}/mastra/api${path}`

// All Mastra requests go through the authenticated backend proxy, which forwards the
// identity headers used to scope memory per organisation + user. The resource id is
// derived server-side from those headers, so the client never sends it.
export const mastraFetch = (path: string, init: RequestInit = {}): Promise<Response> => {
  const headers = new Headers(init.headers)
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return fetch(buildUrl(path), { ...init, headers, credentials: 'include' })
}

// Fetcher for the form-builder floating assistant on a form's preview page: adds the form id
// as a header so the server can scope the memory resource per form (see agents server.ts'
// `buildResourceId`) — editing form A's chat never sees form B's history for the same user.
export const createFormBuilderMastraFetch =
  (formId: string): MastraFetcher =>
  (path, init = {}) => {
    const headers = new Headers(init.headers)
    headers.set('x-form-id', formId)
    return mastraFetch(path, { ...init, headers })
  }

// The agent behind the floating assistant that follows the admin around the app. Not part of
// MASTRA_AGENT_IDS — it never appears in the staff agents hub, it is only reachable from the FAB.
export const PAGE_AGENT_ID = 'page'

// Header values must be Latin-1 (`Headers.set` throws otherwise) and the path is client-supplied,
// so anything outside printable ASCII — including the newlines that would let a crafted URL
// inject a second header — is dropped, and the rest is capped.
const MAX_PAGE_PATH_LENGTH = 256

const NON_PRINTABLE_ASCII = /[^\x20-\x7E]/g

export const sanitizePagePath = (pagePath: string): string =>
  pagePath.replace(NON_PRINTABLE_ASCII, '').slice(0, MAX_PAGE_PATH_LENGTH)

// An inventory much larger than this is a sign the harvester matched something it shouldn't;
// truncating keeps a runaway page from pushing the request past the proxy's header limits.
const MAX_PAGE_CONTROLS_HEADER_LENGTH = 6000

// Fetcher for the page assistant: adds the current URL path as a header so the server can fold
// it into the page agent's system prompt (see agents/page.ts) — the admin's message reaches the
// model exactly as they typed it, with no "[on /case-management/cases]" suffix pasted in.
//
// `getPageControls` is called per request rather than captured once, so the inventory describes
// the page as it is at send time: a filter the user changed since opening the chat is reflected,
// and the ids the agent gets back are the ones the executor will resolve. It is base64 (see
// encodePageControls) because control labels are arbitrary UI text and headers are Latin-1.
export const createPageMastraFetch =
  (pagePath: string, getPageControls?: () => string): MastraFetcher =>
  (path, init = {}) => {
    const headers = new Headers(init.headers)
    const value = sanitizePagePath(pagePath)
    if (value) headers.set('x-page-path', value)

    const controls = getPageControls?.()
    if (controls && controls.length <= MAX_PAGE_CONTROLS_HEADER_LENGTH) {
      headers.set('x-page-controls', controls)
    }

    return mastraFetch(path, { ...init, headers })
  }

export interface UploadedMastraAttachment {
  id: string
  mimeType: string
  size: number
  name: string
}

// The `mastra-attachment` backend module (sibling to `document/`) that stores chat attachments
// in S3 and hands back a small JSON reference. It's a plain NestJS module mounted at the API
// root — unlike the durable-agent endpoints above, it is not proxied through `/mastra/api` — so
// it's reached directly rather than via `mastraFetch`/`buildUrl`.
const MASTRA_ATTACHMENT_UPLOAD_PATH = '/mastra-attachment'
const MASTRA_ATTACHMENT_UPLOAD_PUBLIC_PATH = '/mastra-attachment/public'

const buildAttachmentFormData = (file: File | Blob, name: string): FormData => {
  const formData = new FormData()
  formData.append('file', file, name)
  return formData
}

const parseUploadedAttachment = async (response: Response): Promise<UploadedMastraAttachment> => {
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return (await response.json()) as UploadedMastraAttachment
}

const buildStaffApiUrl = (path: string): string => `${import.meta.env.PUBLIC_API_URL}${path}`

// Staff-authenticated attachment upload. Same bearer-token + `credentials: include` pattern as
// `mastraFetch` above (this endpoint is a plain, non-Mastra-proxied backend module, so it's
// reached directly rather than through `mastraFetch`'s `/mastra/api` base path).
export const uploadMastraAttachment = async (
  file: File | Blob,
  name: string,
): Promise<UploadedMastraAttachment> => {
  const headers = new Headers()
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(buildStaffApiUrl(MASTRA_ATTACHMENT_UPLOAD_PATH), {
    method: 'POST',
    headers,
    credentials: 'include',
    body: buildAttachmentFormData(file, name),
  })
  return parseUploadedAttachment(response)
}

const buildPublicApiUrl = (path: string): string => `${import.meta.env.PUBLIC_API_URL}${path}`

// Public verification-link attachment upload — authenticated the same way the other public
// endpoints this widget/backend expose are (`x-user-client-token` header), mirroring
// `document.controller.ts`'s public-by-user-client shape.
export const uploadMastraAttachmentPublic = async (
  file: File | Blob,
  name: string,
  userClientToken: string,
): Promise<UploadedMastraAttachment> => {
  const response = await fetch(buildPublicApiUrl(MASTRA_ATTACHMENT_UPLOAD_PUBLIC_PATH), {
    method: 'POST',
    headers: { 'x-user-client-token': userClientToken },
    credentials: 'include',
    body: buildAttachmentFormData(file, name),
  })
  return parseUploadedAttachment(response)
}

export const USER_CLIENT_CHAT_KIND = {
  formAssistant: 'userClient',
  formChat: 'userClientFormChat',
} as const

export type UserClientChatKind = (typeof USER_CLIENT_CHAT_KIND)[keyof typeof USER_CLIENT_CHAT_KIND]

const CHAT_THREAD_PATH = '/verification/public/for-user-client/chat-thread'

export interface ChatThreadRequest {
  chat: UserClientChatKind
  formId: string
  userClientToken: string
  localThreadId?: string
}

const requestChatThread = async (
  path: string,
  method: 'PUT' | 'POST',
  { chat, formId, userClientToken, localThreadId }: ChatThreadRequest,
): Promise<string> => {
  const response = await fetch(buildPublicApiUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json', 'x-user-client-token': userClientToken },
    credentials: 'include',
    body: JSON.stringify({ chat, formId, ...(localThreadId ? { threadId: localThreadId } : {}) }),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const { threadId } = (await response.json()) as { threadId: string }
  return threadId
}

export const resolveUserClientChatThread = (request: ChatThreadRequest): Promise<string> =>
  requestChatThread(CHAT_THREAD_PATH, 'PUT', request)

export const resetUserClientChatThread = (
  request: Omit<ChatThreadRequest, 'localThreadId'>,
): Promise<string> => requestChatThread(`${CHAT_THREAD_PATH}/reset`, 'POST', request)

export interface OnboardingChatThreadRequest {
  threadId: string
  formId: string
  userClientToken: string
}

// Hands the /onboarding conversation over to the verification opened from it, signed in by the
// onboarding cookie, and returns that verification's assistant thread: the handed-over one, unless
// the verification already had its own. The form page then shows the same conversation.
export const handOverOnboardingChatThread = async ({
  threadId,
  formId,
  userClientToken,
}: OnboardingChatThreadRequest): Promise<string> => {
  const response = await fetch(buildPublicApiUrl('/onboarding/chat-thread'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-user-client-token': userClientToken },
    credentials: 'include',
    body: JSON.stringify({ threadId, formId }),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const { threadId: verificationThreadId } = (await response.json()) as { threadId: string }
  return verificationThreadId
}

export const MASTRA_CHAT_KIND = {
  formBuilder: 'formBuilder',
  page: 'page',
} as const

export type MastraChatKind = (typeof MASTRA_CHAT_KIND)[keyof typeof MASTRA_CHAT_KIND]

const MASTRA_CHAT_THREAD_PATH = '/mastra-chat-thread'

export interface MastraChatThreadRequest {
  chat: MastraChatKind
  formId?: string
  // Scopes a `page` chat to one admin page, so each page keeps its own stored conversation.
  pagePath?: string
  localThreadId?: string
}

const requestMastraChatThread = async (
  path: string,
  method: 'PUT' | 'POST',
  { chat, formId, pagePath, localThreadId }: MastraChatThreadRequest,
): Promise<string> => {
  const headers = new Headers({ 'Content-Type': 'application/json' })
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(buildStaffApiUrl(path), {
    method,
    headers,
    credentials: 'include',
    body: JSON.stringify({
      chat,
      ...(formId ? { formId } : {}),
      ...(pagePath ? { pagePath } : {}),
      ...(localThreadId ? { threadId: localThreadId } : {}),
    }),
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const { threadId } = (await response.json()) as { threadId: string }
  return threadId
}

export interface PageChatThreadSummary {
  pagePath: string
  threadId: string
  updatedAt: string
}

export const listPageChatThreads = async (): Promise<PageChatThreadSummary[]> => {
  const headers = new Headers()
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(buildStaffApiUrl(`${MASTRA_CHAT_THREAD_PATH}/pages`), {
    headers,
    credentials: 'include',
  })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  return (await response.json()) as PageChatThreadSummary[]
}

export const resolveMastraChatThread = (request: MastraChatThreadRequest): Promise<string> =>
  requestMastraChatThread(MASTRA_CHAT_THREAD_PATH, 'PUT', request)

export const resetMastraChatThread = (
  request: Omit<MastraChatThreadRequest, 'localThreadId'>,
): Promise<string> => requestMastraChatThread(`${MASTRA_CHAT_THREAD_PATH}/reset`, 'POST', request)

// The agent user clients (end customers on a verification link) talk to via the public proxy.
// Not part of MASTRA_AGENT_IDS — it never appears in the staff agents hub.
export const USER_CLIENT_AGENT_ID = 'user-client'

// Chat-only variant of the user-client agent: drives the whole verification conversationally
// (no visual form alongside it) on the /forms/chat/$id page. Same public proxy/token auth as
// USER_CLIENT_AGENT_ID — only the addressed agent id differs.
export const USER_CLIENT_FORM_CHAT_AGENT_ID = 'user-client-form-chat'

// Fetcher for the public user-client chat: authenticated by the verification-link token via
// the public backend proxy, which locks requests to the user-client agent and derives the
// memory resource id from the token server-side.
export const createUserClientMastraFetch = (userClientToken: string): MastraFetcher => {
  const buildPublicUrl = (path: string): string =>
    `${import.meta.env.PUBLIC_API_URL}/mastra/public/api${path}`
  return (path, init = {}) => {
    const headers = new Headers(init.headers)
    headers.set('x-user-client-token', userClientToken)
    return fetch(buildPublicUrl(path), { ...init, headers })
  }
}

// The public onboarding assistant on /onboarding. Not part of MASTRA_AGENT_IDS — it is only
// reachable through the backend's onboarding proxy, never from the staff agents hub.
export const ONBOARDING_AGENT_ID = 'onboarding'

// Fetcher for the onboarding chat: the visitor's session is the httpOnly onboarding cookie, so
// the request only needs to carry credentials. The proxy locks it to the onboarding agent and
// derives the memory resource id from the verified email. Opened from an organisation's own
// onboarding link, the organisation id rides along so the agent starts with it selected — it is
// prompt context only, the agent's tools still check the organisation accepts onboarding.
export const createOnboardingMastraFetch =
  (organisationId?: string): MastraFetcher =>
  (path, init = {}) => {
    const headers = new Headers(init.headers)
    if (organisationId) headers.set('x-onboarding-organisation-id', organisationId)
    return fetch(`${import.meta.env.PUBLIC_API_URL}/mastra/onboarding/api${path}`, {
      ...init,
      headers,
      credentials: 'include',
    })
  }

// The supervisor is a durable agent, and durable agents do not honour Memory's
// `generateTitle` option — new threads are persisted without a title, so the chat list
// shows "Untitled chat" after a reload. We derive a readable title from the first user
// message and persist it ourselves.
const MAX_TITLE_LENGTH = 60

export const deriveThreadTitle = (text: string): string => {
  const normalised = text.replace(/\s+/g, ' ').trim()
  if (normalised.length <= MAX_TITLE_LENGTH) return normalised
  return `${normalised.slice(0, MAX_TITLE_LENGTH).trimEnd()}…`
}

// Persist a thread title via Mastra's memory REST endpoint. Resource scoping is derived
// server-side from the identity headers the proxy forwards, so no resource id is sent.
export const updateThreadTitle = (
  threadId: string,
  title: string,
  agentId: string,
  fetcher: MastraFetcher = mastraFetch,
): Promise<Response> =>
  fetcher(`/memory/threads/${threadId}?agentId=${agentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  })

export const createThreadId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`

// A run id the client owns up-front, so it can later reconnect to the same durable run.
export const createRunId = (): string => createThreadId()

// While a turn is generating we remember its run id per thread, so a page refresh or a
// dropped connection can reconnect via `observe` instead of losing the in-flight answer.
// Durable runs time out server-side at 5 min and their cached events are dropped shortly
// after; the TTL keeps a stale pointer from triggering a doomed reconnect.
const ACTIVE_RUN_PREFIX = 'mastra:active-run:'
const ACTIVE_RUN_TTL_MS = 10 * 60 * 1000

export interface ActiveRun {
  runId: string
  startedAt: number
  // The triggering user message text. Mastra persists the turn only at finish, so on a
  // mid-generation reload this is the only copy left to re-render the user's question.
  userText: string
}

export const getActiveRun = (threadId: string): ActiveRun | null => {
  try {
    const raw = localStorage.getItem(`${ACTIVE_RUN_PREFIX}${threadId}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ActiveRun
    if (!parsed?.runId || Date.now() - parsed.startedAt > ACTIVE_RUN_TTL_MS) {
      localStorage.removeItem(`${ACTIVE_RUN_PREFIX}${threadId}`)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

export const setActiveRun = (threadId: string, runId: string, userText: string): void => {
  try {
    localStorage.setItem(
      `${ACTIVE_RUN_PREFIX}${threadId}`,
      JSON.stringify({ runId, startedAt: Date.now(), userText } satisfies ActiveRun),
    )
  } catch {
    // Storage may be unavailable (private mode / quota); resumability is best-effort.
  }
}

export const clearActiveRun = (threadId: string): void => {
  try {
    localStorage.removeItem(`${ACTIVE_RUN_PREFIX}${threadId}`)
  } catch {
    // ignore
  }
}
